import React, { useEffect, createElement } from 'react';
interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  article?: {
    publishedTime?: string;
    author?: string;
    section?: string;
  };
  noindex?: boolean;
}
export function SEO({
  title,
  description,
  canonical,
  ogImage = 'https://kraftcrm.de/og-image.jpg',
  ogType = 'website',
  article,
  noindex = false
}: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;
    // Update or create meta tags
    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    // Add/update favicon links
    const updateFavicon = (rel: string, sizes: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"][sizes="${sizes}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('sizes', sizes);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    // Standard favicon
    let faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (!faviconLink) {
      faviconLink = document.createElement('link');
      faviconLink.setAttribute('rel', 'icon');
      faviconLink.setAttribute('type', 'image/png');
      document.head.appendChild(faviconLink);
    }
    faviconLink.setAttribute('href', '/favicon.png');
    // Apple touch icon
    updateFavicon('apple-touch-icon', '180x180', '/favicon.png');
    // Android/Chrome icons
    updateFavicon('icon', '192x192', '/favicon.png');
    updateFavicon('icon', '512x512', '/favicon.png');
    // Microsoft tile
    updateMetaTag('msapplication-TileImage', '/favicon.png');
    updateMetaTag('msapplication-TileColor', '#0a0e1a');
    // Theme color for mobile browsers
    updateMetaTag('theme-color', '#0a0e1a');
    // Basic meta tags
    updateMetaTag('description', description);
    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', ogImage, true);
    if (canonical) {
      updateMetaTag('og:url', canonical, true);
    }
    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', ogImage, true);
    // Article-specific tags
    if (article && ogType === 'article') {
      if (article.publishedTime) {
        updateMetaTag('article:published_time', article.publishedTime, true);
      }
      if (article.author) {
        updateMetaTag('article:author', article.author, true);
      }
      if (article.section) {
        updateMetaTag('article:section', article.section, true);
      }
    }
    // Robots meta tag
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      const robotsTag = document.querySelector('meta[name="robots"]');
      if (robotsTag) {
        robotsTag.remove();
      }
    }
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }
    // Add hreflang tags for multilingual support
    const addHreflangTag = (lang: string, href: string) => {
      let hreflangLink = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`) as HTMLLinkElement;
      if (!hreflangLink) {
        hreflangLink = document.createElement('link');
        hreflangLink.setAttribute('rel', 'alternate');
        hreflangLink.setAttribute('hreflang', lang);
        document.head.appendChild(hreflangLink);
      }
      hreflangLink.setAttribute('href', href);
    };
    // Add hreflang tags if canonical is provided
    if (canonical) {
      // German version (default)
      addHreflangTag('de', canonical);
      // English version
      const enUrl = canonical.includes('?') ? `${canonical}&lang=en` : `${canonical}?lang=en`;
      addHreflangTag('en', enUrl);
      // x-default (fallback)
      addHreflangTag('x-default', canonical);
    }
    // Update language
    const htmlElement = document.documentElement;
    const lang = canonical?.includes('lang=en') ? 'en' : 'de';
    htmlElement.setAttribute('lang', lang);
  }, [title, description, canonical, ogImage, ogType, article, noindex]);
  return null;
}